import React from 'react';
import { DefaultTheme } from '@monorepo-web/styles';
import {
  TabelaAplicacoesResgatePDF,
  TabelaEstatisticasPDF,
  TabelaFluxoAtivosPDF,
  TabelaFluxoResgatePDF,
  TabelaInformacoesAdicionaisPDF,
  TabelaPosicaoConsolidadaPDF,
  TabelaPosicaoRendimentoPDF,
  TabelaResumoMovimentacoesPDF,
  TabelaRetornoPDF,
  TabelaRetornosNominaisPDF
} from '@monorepo-web/templates';
import { IPreferenceCustomizacao } from '@monorepo-web/logic-extrato';
import { ComponentPDF, docJSPDF, IConfig } from '@monorepo-web/pdf-generator';
import { IPageData } from '../Pages/IPageData';

/**
 * Gera Objeto com os componentes do PDF para as tabelas
 * @param pageData Dados das páginas
 * @param theme Tema
 * @param configs Objeto com as configurações de cada componente disponível
 * @param preferenceCustomizacao Customizações
 * @param moneyPrefix Unidade monetária
 *
 * @returns Componentes do PDF para as tabelas que serão utilizados no PDF
 */
export function gerarTabelas(pageData: IPageData, theme: DefaultTheme, docObj: docJSPDF, configs: Record<string, IConfig>, preferenceCustomizacao: IPreferenceCustomizacao | null, moneyPrefix: string) {
  const tabelas: Record<string, ComponentPDF | null> = {
    'Retorno': TabelaRetornoPDF({
      docObj: docObj,
      config: configs.Retorno,
      theme: theme,
      rows: pageData?.ResCart?.rowsRetorno,
      obj_datas: pageData?.ResCart?.obj_datas,
      periodo: pageData?.ResCart?.periodo,
      useBold: preferenceCustomizacao?.ResCart_boldRetornoNomial
    }),
    'FluxoResgate': TabelaFluxoResgatePDF({
      docObj: docObj,
      config: configs.FluxoResgate,
      theme: theme,
      rows: pageData?.ResCart?.rowsLiq,
    }),
    'PosicaoRendimento': TabelaPosicaoRendimentoPDF({
      docObj: docObj,
      config: configs.PosicaoRendimento,
      theme: theme,
      rows: pageData?.ResCart?.rowsClasse,
      layout: preferenceCustomizacao?.ResCart_percent ? 'percent' : 'default',
      moneyPrefix
    }),
    'ResumoMovimentacoes': TabelaResumoMovimentacoesPDF({
      docObj: docObj,
      config: configs.ResumoMovimentacoes,
      theme: theme,
      rows: pageData?.ResCart?.rowsMovim,
    }),
    'RetornosNominais': TabelaRetornosNominaisPDF({
      docObj: docObj,
      config: configs.RetornosNominais,
      theme: theme,
      rows: pageData?.PerfHist?.rowsRetornos,
      dadosMesAMesAgrupados: pageData?.PerfHist?.dadosMesaMesAgrupados,
    }),
    'AplicacoesResgate': TabelaAplicacoesResgatePDF({
      docObj: docObj,
      config: configs.AplicacoesResgate,
      theme: theme,
      rows: pageData?.PerfHist?.rowsMovim,
      titles: preferenceCustomizacao?.PerfHist_arrayTabelaAplicacoesResgate ? JSON.parse(preferenceCustomizacao?.PerfHist_arrayTabelaAplicacoesResgate) : null
    }),
    'PosicaoConsolidada': TabelaPosicaoConsolidadaPDF({
      docObj: docObj,
      config: configs.PosicaoConsolidada,
      theme: theme,
      rows: pageData?.PosCon?.rowsDados,
      datas: pageData?.PosCon?.datas_dados_ativos_agrupados,
      headerPercent: preferenceCustomizacao?.PosCon_headerPercent,
      firstColWidth: preferenceCustomizacao?.PDF_firstColWidthPosCon,
      useLegendColor: preferenceCustomizacao?.PDF_colorLegendPosCon,
      layout: 'default',
    }),
    'RetornoAtivo': TabelaPosicaoConsolidadaPDF({
      docObj: docObj,
      config: configs.RetornoAtivo,
      theme: theme,
      rows: pageData?.RetornoAtivo?.rowsDados,
      headerPercent: preferenceCustomizacao?.PosCon_headerPercent,
      firstColWidth: preferenceCustomizacao?.PDF_firstColWidthPosCon,
      useLegendColor: preferenceCustomizacao?.PDF_colorLegendPosCon,
      layout: preferenceCustomizacao?.RetAtivo_comSaldoBruto ? 'retorno_ativo_saldo_bruto' : 'retorno_ativo',
    }),
    'Explosao': TabelaPosicaoConsolidadaPDF({
      docObj: docObj,
      config: configs.Explosao,
      theme: theme,
      rows: pageData?.CartExp?.rowsDadosAgrupados,
      layout: 'carteira_explodida',
    }),
    'FluxoAtivos': TabelaFluxoAtivosPDF({
      docObj: docObj,
      config: configs.FluxoAtivos,
      theme: theme,
      rows: pageData?.FluxoAtivo?.rowsFluxoAtivos,
    }),
    'info1': TabelaInformacoesAdicionaisPDF({
      docObj: docObj,
      config: configs.info1,
      theme: theme,
      titulo: 'Informações Adicionais',
      rows: pageData?.EstatAvancada?.rowsInfos1,
      bench: pageData?.EstatAvancada?.bench,
    }),
    'info2': TabelaInformacoesAdicionaisPDF({
      docObj: docObj,
      config: configs.info2,
      theme: theme,
      titulo: ' ',
      layout: 'info2',
      rows: pageData?.EstatAvancada?.rowsInfos2,
    }),
    'retorno_estat': TabelaEstatisticasPDF({
      docObj: docObj,
      config: configs.retorno_estat,
      rows: pageData?.EstatAvancada?.rowsRetornos,
      benches: pageData?.EstatAvancada?.benches,
      theme: theme,
      titulo: 'Retorno',
    }),
    'volatilidade': TabelaEstatisticasPDF({
      docObj: docObj,
      config: configs.volatilidade,
      rows: pageData?.EstatAvancada?.rowsVol,
      theme: theme,
      benches: pageData?.EstatAvancada?.benches,
      titulo: 'Volatilidade',
    }),
    'IndiceSharpe': TabelaEstatisticasPDF({
      docObj: docObj,
      config: configs.IndiceSharpe,
      rows: pageData?.EstatAvancada?.rowsSharpe,
      theme: theme,
      benches: pageData?.EstatAvancada?.benches,
      titulo: 'Índice Sharpe',
    }),
  }

  // seleciona as tabelas utilizadas no PDF
  const tabelasFilter: Record<string, ComponentPDF | null> = Object.entries(tabelas)
    .filter(([graficoKey]) => configs[graficoKey])
    .reduce((acc, [key, component]) => {
      acc[key] = component;
      return acc;
    }, {} as Record<string, ComponentPDF | null>);

  return tabelasFilter;
}
